import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, Box, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import LandingNav from '@/components/landing/LandingNav';
import LandingFooter from '@/components/landing/LandingFooter';
import PageMeta from '@/components/landing/PageMeta';
import ApexHeroLogo from '@/components/landing/ApexHeroLogo';
import IncidentCard from '@/components/landing/IncidentCard';
import AccessRequestModal, { useAccessGate } from '@/components/landing/AccessRequestModal';
import GovernanceContinuum from '@/components/landing/GovernanceContinuum';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] },
});

const ENGINES = [
  {
    name: 'Identity Proof Engine',
    icon: Eye,
    body: 'Granted vs used vs needed access. Proves exactly what each identity could do, did do, and should have done — at any point in time.',
    signals: 'IAM policies · CloudTrail · session tokens',
  },
  {
    name: 'Container Governance Engine',
    icon: Box,
    body: 'Proves which image ran, where it came from, whether it was approved, and what it accessed during every second it was running.',
    signals: 'ECR metadata · Inspector · CloudTrail',
  },
  {
    name: 'Environment Integrity Engine',
    icon: Shield,
    body: 'Proves what crossed from dev to production, what the blast radius was, and whether contamination was legitimate.',
    signals: 'Config rules · VPC flows · change events',
  },
];

const DEPLOY = [
  ['Deploys in your AWS account',         'CloudFormation · one stack'],
  ['Serverless — no servers to manage',   'Lambda · Athena · DynamoDB'],
  ['Read-only access to source data',     'No write permissions · ever'],
  ['First governance snapshot',           'Under 30 minutes'],
  ['Your data never leaves',              'S3 · KMS · your account'],
];

export default function ProductLens() {
  const navigate = useNavigate();
  const gateOpen = useAccessGate();
  const [open, setOpen] = useState(false);

  const requestLens = useCallback(() => {
    if (gateOpen) { navigate('/lens'); return; }
    setOpen(true);
  }, [gateOpen, navigate]);

  return (
    <div style={{ background: '#06070A', minHeight: '100vh', color: '#F1F5F9' }}>
      <PageMeta
        title="APEX Lens — Cloud Governance Proof"
        description="Proves what your cloud was doing — and reconstructs the exact governance state that existed at that moment. Designed for the investigations that are coming."
        path="/product/lens"
      />
      <LandingNav />

      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '120px 24px 64px', position: 'relative' }}>
        {/* Hero */}
        <motion.div {...fadeUp(0)}>
          <div style={{ marginBottom: 28 }} aria-hidden>
            <ApexHeroLogo width={200} variant="lens" showWordmark={false} showTagline={false} />
          </div>
          <h1 style={{ fontSize: 'clamp(48px, 8vw, 80px)', fontWeight: 800, letterSpacing: '-0.03em', color: '#0D9488', margin: 0 }}>APEX LENS</h1>
          <p style={{ fontSize: 13, color: '#0D9488', fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.18em', marginTop: 8 }}>
            Cloud Governance Proof Platform
          </p>
          <p style={{ fontSize: 18, color: '#94A3B8', maxWidth: 720, marginTop: 20, lineHeight: 1.6 }}>
            Proves what your cloud was doing. At any point in time.<br />
            Reconstruction on demand. Signed evidence for any audit.
          </p>

          <div style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
            <button onClick={requestLens} style={{ background: '#0D9488', color: 'white', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer' }} data-testid="lens-request-hero">
              Request access <ArrowRight size={14} />
            </button>
            <Link to="/lens/how-it-works" style={{ border: '1px solid #1E2A45', color: '#94A3B8', padding: '12px 22px', borderRadius: 10, fontSize: 13, textDecoration: 'none' }}>
              How it works
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Incident Card */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px 16px' }}>
        <IncidentCard basePath="/lens" />
        <p style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 10,
          color: '#334155',
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          textAlign: 'center',
          marginTop: 28,
          marginBottom: 0,
        }} data-testid="lens-inevitability-line">
          This is not an edge case. This is your organization.
        </p>
      </section>

      {/* Three Evidence Engines */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '64px 24px 96px' }}>
        <motion.div {...fadeUp(0)}>
          <h2 style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 24 }}>
            Three Evidence Engines
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {ENGINES.map((e, i) => {
            const Icon = e.icon;
            return (
              <motion.div key={e.name} {...fadeUp(0.1 * i)} style={{
                background: '#0F1629', border: '1px solid #1E2A45', borderTop: '3px solid #0D9488',
                borderRadius: 14, padding: 24,
              }}>
                <Icon size={22} color="#0D9488" />
                <h3 style={{ fontSize: 16, fontWeight: 700, marginTop: 14, color: '#F8FAFC' }}>{e.name}</h3>
                <p style={{ fontSize: 13, color: '#94A3B8', lineHeight: 1.6, marginTop: 10 }}>{e.body}</p>
                <p style={{ fontSize: 10, color: '#475569', fontFamily: 'JetBrains Mono, monospace', marginTop: 14, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Signals: {e.signals}
                </p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* The Temporal Claim */}
      <section style={{ background: '#0F1629', borderTop: '1px solid #1E2A45', borderBottom: '1px solid #1E2A45', padding: '80px 24px' }}>
        <motion.div {...fadeUp(0)} style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
          <p style={{ fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 700, color: '#F8FAFC', lineHeight: 1.4, letterSpacing: '-0.01em' }}>
            “History is the missing layer of governance.<br />
            <span style={{ color: '#0D9488' }}>Apex Lens adds it.</span>”
          </p>
          <div style={{ marginTop: 36, color: '#94A3B8', fontSize: 14, lineHeight: 1.9 }}>
            <div>Show any point in time.</div>
            <div>Prove what your cloud state was.</div>
            <div>Answer any audit question.</div>
            <div style={{ color: '#64748B', marginTop: 10, fontSize: 13 }}>No manual investigation. No reassembly of logs. A complete reconstruction on demand.</div>
          </div>
          <p style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
            color: '#0D9488',
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            marginTop: 28,
            textAlign: 'center',
          }} data-testid="lens-proofgrade-line">
            Proof-grade cloud governance telemetry.
          </p>
        </motion.div>
      </section>

      {/* Quiet Continuum separator before Deployment Facts — clean state, Lens cadence */}
      <div style={{ marginBottom: 0 }}>
        <GovernanceContinuum height={120} opacity={0.3} variant="lens" showLabels={false} />
      </div>

      {/* Deployment Facts */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '80px 24px' }}>
        <motion.h2 {...fadeUp(0)} style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.18em', fontFamily: 'JetBrains Mono, monospace', marginBottom: 12 }}>
          Deployment Facts
        </motion.h2>
        <motion.p {...fadeUp(0.05)} style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 12,
          color: '#0D9488',
          marginBottom: 20,
          marginTop: 0,
          letterSpacing: '0.06em',
        }}>
          Managed like SaaS. Deployed in your environment.
        </motion.p>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {DEPLOY.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', borderTop: '1px solid #1E2A45', padding: '14px 0' }}>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#94A3B8' }}>{k}</span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#0D9488', textAlign: 'right' }}>{v}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px 120px' }}>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={requestLens} style={{ background: '#0D9488', color: 'white', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer' }} data-testid="lens-request-bottom">
            Request access <ArrowRight size={14} />
          </button>
          <Link to="/contact" style={{ border: '1px solid #0D9488', color: '#0D9488', padding: '12px 22px', borderRadius: 10, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
            Become a design partner →
          </Link>
          <Link to="/platform" style={{ border: '1px solid #1E2A45', color: '#94A3B8', padding: '12px 22px', borderRadius: 10, fontSize: 13, textDecoration: 'none' }}>
            Platform architecture →
          </Link>
        </div>
      </section>

      <AccessRequestModal
        open={open}
        product="Apex Lens"
        onClose={() => setOpen(false)}
        onGranted={() => { setOpen(false); navigate('/lens'); }}
      />
      <LandingFooter />
    </div>
  );
}
